import { userAtom } from '@/global-store';
import useLogoutFn from '@/hooks/useLogoutFn';
import ChangePasswordDialog from './ChangePasswordDialog';
import ProfileEditDialog from '@/layout/Header/ProfileEditDialog';
import { KeyRound, LogOut, User, Copy, Check } from 'lucide-react';
import { useAtom } from 'jotai';
import { useId, useState } from 'react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Typography,
  useTailwindBreakpoint,
} from '@eye-able/ui';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { T } from '@/i18n/T';
import type { BrandButtonProps } from '@/branding/useOrgBranding';

function ProfileSection({ brandProps }: { brandProps?: BrandButtonProps }) {
  const { t } = useInlineTranslation();
  const [user] = useAtom(userAtom);
  const logoutFn = useLogoutFn();
  const isDesktop = useTailwindBreakpoint('xl');

  const [copied, setCopied] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const apiKeyLabelId = useId();
  const headingId = useId();

  const handleCopyApiKey = async () => {
    if (!user?.apiKey) return;
    await navigator.clipboard.writeText(user.apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const userInfo = {
    name: {
      label: <T k={'name'} />,
      value: user?.firstName + ' ' + user?.lastName,
    },
    email: { label: <T k={'email'} />, value: user?.email },
    phone: { label: <T k={'phone'} />, value: user?.phone },
    company: { label: <T k={'company'} />, value: user?.company },
  };

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type='button'
            variant='ghost'
            size={isDesktop ? 'default' : 'icon'}
            {...brandProps}
          >
            <User aria-hidden />
            <span className='eye:max-xl:sr-only'>{t('profile')}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          // lower z-index to be below other modals from this popover
          className='eye:z-1300 eye:p-s! eye:pt-m eye:max-w-[400px] eye:max-h-(--radix-popover-content-available-height)'
          align={'end'}
          matchTriggerWidth={false}
          collisionPadding={16}
          aria-labelledby={headingId}
        >
          <Typography id={headingId} tag='h2' variant='headline-s'>
            <T k='profileHeader' />
          </Typography>
          <dl className='eye:mt-2xs eye:mb-s eye:flex eye:flex-col eye:gap-3xs'>
            {Object.entries(userInfo).map(([key, { label, value }]) => (
              <div key={key}>
                <Typography tag='dt' variant='body-s-highlight'>
                  {label}
                </Typography>
                <Typography tag='dd' className='eye:break-all'>
                  {value || ' - '}
                </Typography>
              </div>
            ))}
          </dl>
          <div
            role='group'
            aria-labelledby={apiKeyLabelId}
            className='eye:mb-s'
          >
            <Typography id={apiKeyLabelId} variant='body-s-highlight'>
              <T k='key' />
            </Typography>
            <div className='eye:flex eye:items-center eye:gap-2xs'>
              <Typography aria-hidden className='eye:break-all eye:flex-1'>
                {'•'.repeat(24)}
              </Typography>
              <span className='eye:sr-only'>
                <T k='hidden' />
              </span>
              <button
                type='button'
                onClick={() => void handleCopyApiKey()}
                aria-label={t('copy')}
                disabled={!navigator.clipboard}
                className='eye:shrink-0 eye:cursor-pointer eye:rounded eye:p-3xs eye:text-current eye:transition-colors eye:hover:bg-black/8 eye:disabled:cursor-not-allowed eye:disabled:opacity-40'
              >
                {copied ? (
                  <Check size={16} aria-hidden />
                ) : (
                  <Copy size={16} aria-hidden />
                )}
              </button>
            </div>
          </div>
          <ul className='eye:flex eye:flex-col eye:gap-2xs'>
            <li className='eye:flex eye:flex-col'>
              <ProfileEditDialog user={user} />
            </li>

            <li className='eye:flex eye:flex-col'>
              <Button
                type='button'
                variant='outline'
                className='eye:w-full'
                onClick={() => setChangePasswordOpen(true)}
              >
                <KeyRound aria-hidden />
                {t('changePassword')}
              </Button>
            </li>

            <li className='eye:flex eye:flex-col'>
              <Button
                type='button'
                onClick={() => void logoutFn()}
                variant='outline'
              >
                <LogOut aria-hidden />
                {t('logOutSB')}
              </Button>
            </li>
          </ul>
        </PopoverContent>
      </Popover>
      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
      />
    </>
  );
}

export default ProfileSection;
